import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect, useRef, type ReactNode } from 'react';

import { useSession } from '@platform/api-client/react';
import { Alert, Card, CardBody, PageHeader, Spinner } from '@platform/ui';

import { NOT_STAFF_DETAIL, NOT_STAFF_HEADING } from './describe-error';
import { loginPathFor } from './next-path';
import { usePermissions } from './permissions';

/**
 * The route guards (M2.7, rules §17.2, §22.2, §22.5).
 *
 * All three are **rendering** guards and none is an authorization one. Every endpoint
 * behind them enforces its own access on the server; what they do is stop a staff member
 * being shown an empty app and a stack of failed requests.
 *
 * `MFA_ENROLMENT_PATHS` is the list a session with no second factor may still reach. It
 * matches the server's `@AllowWithoutMfa()` set (SECURITY.md §4.3): the enrolment screen
 * itself, the sign-in screen, and nothing else - because a staff session without MFA
 * answers `403 MFA_REQUIRED` on every other route, and rendering a dashboard that cannot
 * load is worse than rendering the one screen that can.
 */

/** Paths reachable before MFA enrolment is complete. */
export const MFA_ENROLMENT_PATHS = ['/login', '/mfa/enroll'];

/**
 * M13.7. Every guarded route keeps its `h1` in the two states where the screen itself
 * never renders: a screen with no level-1 heading has no name a reader can navigate to,
 * and `/settings/domains` and `/feedback` were reaching axe in exactly that state
 * (WCAG 1.3.1, 2.4.6). The heading is the guard's own, because the guard is what is on
 * screen.
 */
function Pending({ label }: { label: string }) {
  return (
    /* `role="status"` on the wrapper, and the spinner silent: the heading IS the status
       message, so announcing it twice — once as an `h1`, once as the spinner's `sr-only`
       label — would be the same sentence read out to nobody's benefit. */
    <div role="status">
      <PageHeader title={label} />
      <Card className="p-6">
        <Spinner label={null} />
      </Card>
    </div>
  );
}

export interface RequireAuthProps {
  children: ReactNode;
}

/**
 * Four states, all deliberate:
 *
 * - **pending** - the boot call is in flight. A spinner, because the app has a full
 *   shell behind it and a blank main area reads as broken.
 * - **anonymous** - redirect to `/login?next=…`, replacing the history entry so Back
 *   does not bounce between the guard and the sign-in screen. Latched, because the guard
 *   is still mounted while the router transitions.
 * - **error** - the session call itself failed. An alert, **not** a redirect: sending
 *   someone to a sign-in screen that also cannot reach the API is a loop.
 * - **signed in but not clinic staff** - says so. A patient account signing in on the
 *   clinic host succeeds at Better Auth and is then refused on every `/admin/*` route;
 *   this turns that into one sentence instead of a screen full of 403s.
 *
 * The population check makes one allowance: a support session raised by "View as" lands
 * here as the **target** clinic user, so `impersonation` being present is itself proof
 * this session belongs in a clinic app (SECURITY.md §6.3, and
 * `ImpersonationService.start` refuses a platform target outright).
 */
export function RequireAuth({ children }: RequireAuthProps) {
  const navigate = useNavigate();
  const session = useSession();
  const location = useRouterState({ select: (state) => state.location });

  const authenticated = session.data?.authenticated === true;
  const anonymous = session.isSuccess && !authenticated;

  const redirected = useRef(false);
  useEffect(() => {
    if (!anonymous || redirected.current) return;
    redirected.current = true;
    void navigate({
      to: loginPathFor(location.pathname, location.searchStr),
      replace: true,
    });
  }, [anonymous, navigate, location.pathname, location.searchStr]);

  if (session.isPending) return <Pending label="Checking your sign-in" />;

  if (session.isError) {
    return (
      <Alert tone="error" title="We could not check your sign-in.">
        {session.error.message} Please refresh the page.
      </Alert>
    );
  }

  // The redirect above is already scheduled; render nothing rather than a flash of the
  // protected screen.
  if (!authenticated) return null;

  const impersonated = session.data.impersonation !== null;
  if (session.data.user?.population !== 'staff' && !impersonated) {
    return (
      <Alert tone="error" title={NOT_STAFF_HEADING}>
        {NOT_STAFF_DETAIL}
      </Alert>
    );
  }

  return <>{children}</>;
}

export interface RequireMfaProps {
  children: ReactNode;
}

/**
 * The enrolment gate (SECURITY.md §4.3, FR-ADMIN-01, rules §21.11).
 *
 * "Staff sign-in with mandatory MFA" is a server guard; this is the screen that makes
 * the requirement actionable instead of a wall of `403 MFA_REQUIRED`. Sign-out stays
 * reachable throughout, because a staff member who cannot enrol right now - wrong phone,
 * no authenticator to hand - must still be able to leave, and the server allows exactly
 * that.
 */
export function RequireMfa({ children }: RequireMfaProps) {
  const navigate = useNavigate();
  const session = useSession();
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  const mfa = session.data?.mfa;
  const mustEnroll = mfa?.required === true && mfa.enrolled !== true;
  const exempt = MFA_ENROLMENT_PATHS.includes(pathname);

  const redirected = useRef(false);
  useEffect(() => {
    if (!mustEnroll || exempt || redirected.current) return;
    redirected.current = true;
    void navigate({ to: '/mfa/enroll', replace: true });
  }, [mustEnroll, exempt, navigate]);

  if (mustEnroll && !exempt) return null;
  return <>{children}</>;
}

export interface RequirePermissionProps {
  /** Every key must be held. Empty means "any signed-in staff member". */
  keys: readonly string[];
  children: ReactNode;
  /** Shown instead of the default refusal, e.g. a read-only version of the screen. */
  fallback?: ReactNode;
}

/**
 * Hides a screen the staff member's role cannot use.
 *
 * It refuses while the session is still loading (rules §22.5: access defaults to denied
 * when the permission cannot be determined) by showing the pending state rather than the
 * screen - never by showing the screen and hoping.
 */
export function RequirePermission({ keys, children, fallback }: RequirePermissionProps) {
  const { canAll, isPending } = usePermissions();

  if (isPending) return <Pending label="Checking your access" />;
  if (canAll(keys)) return <>{children}</>;
  if (fallback !== undefined) return <>{fallback}</>;

  return (
    <>
      {/* M13.7: the refusal is a page in its own right, so it carries the page's h1. */}
      <PageHeader title="No access" />
      <Card>
        <CardBody>
          <Alert tone="warning" title="You do not have access to this.">
            Your role at this clinic does not include this area. Ask a clinic administrator if you
            need it.
          </Alert>
        </CardBody>
      </Card>
    </>
  );
}
